import React from 'react';
import {
  PhoneIcon,
  PlusIcon,
  EyeIcon,
  EyeSlashIcon,
  PencilIcon,
  TrashIcon,
  PlayIcon as ResumeIcon,
  PauseIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  DocumentArrowUpIcon,
  MicrophoneIcon,
  SparklesIcon,
  LockClosedIcon,
  ChartBarIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  UserIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  MagnifyingGlassIcon,
  Bars3Icon,
  UserCircleIcon,
  ShieldCheckIcon,
  CogIcon,
  InformationCircleIcon,
  CheckIcon,
  BellIcon,
  EnvelopeIcon,
  KeyIcon,
  ServerIcon,
  DocumentCheckIcon,
  ChatBubbleLeftRightIcon,
  ExclamationCircleIcon,
  UserGroupIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import {
  Headphones,
  Target,
  Calendar,
  Bot,
  Users,
  BarChart3,
  UserPlus,
  PlayIcon,
} from 'lucide-react';

export const toTitle = (s) =>
  s ? s.toString().replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : '';

export const norm = (s) => (s == null ? '' : s.toString().trim().toLowerCase().replace(/\s+/g, '_'));

export const formatDate = (d, locale = 'en-US') => {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? String(d) : dt.toLocaleString(locale);
};

export const formatRelativeTime = (timestamp, t) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffInHours = (now - date) / (1000 * 60 * 60);

  if (diffInHours < 1) {
    const minutes = Math.floor((now - date) / (1000 * 60));
    return `${minutes} ${t('minutesAgo')}`;
  } else if (diffInHours < 24) {
    return `${Math.floor(diffInHours)} ${t('hoursAgo')}`;
  } else if (diffInHours < 48) {
    return t('yesterday');
  } else {
    return date.toLocaleDateString();
  }
};

export const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

export const clamp0to100 = (v) => Math.min(100, Math.max(0, Math.round(num(v))));

export const compact = (n) =>
  new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(Number(n) || 0);

export const pad = (n) => String(n).padStart(2, '0');

export const toMMSS = (secs) => {
  const s = Math.max(0, num(secs));
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${String(r).padStart(2, '0')}`;
};

export const toHHMMSS = (secs) => {
  if (secs == null || Number.isNaN(Number(secs))) return '—';
  const s = Math.max(0, Math.floor(Number(secs)));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
};

export const parseDuration = (raw) => {
  if (typeof raw === 'number') return toMMSS(raw);
  if (typeof raw === 'string' && /^\d+:\d{2}$/.test(raw)) return raw;
  if (typeof raw === 'string' && /^\d+$/.test(raw)) return toMMSS(Number(raw));
  return raw || '—';
};

export const getStatusColor = (status) => {
  switch (norm(status)) {
    case 'active':
    case 'completed':
    case 'succeeded':
    case 'success':
      return 'bg-green-500/10 text-green-400 border border-green-500/20';
    case 'inactive':
    case 'missed':
    case 'failed':
    case 'error':
      return 'bg-red-500/10 text-red-400 border border-red-500/20';
    case 'in_progress':
    case 'ongoing':
    case 'processing':
    case 'pending':
      return 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20';
    case 'cancelled':
      return 'bg-white/5 text-white/40 border border-white/10';
    default:
      return 'bg-white/5 text-white/40 border border-white/10';
  }
};

export const getStatusBadgeClass = (status) => getStatusColor(status);

export const getTypeIcon = (type) => {
  switch (norm(type)) {
    case 'customer_support':
      return <Headphones className="h-5 w-5" />;
    case 'lead_generation':
      return <Target className="h-5 w-5" />;
    case 'appointment_scheduling':
      return <Calendar className="h-5 w-5" />;
    default:
      return <Bot className="h-5 w-5" />;
  }
};

export const getIconBgColor = (type) => {
  return 'bg-white/5 border border-white/10';
};

export const getIconTextColor = (type) => {
  return 'text-white/70';
};

export const getTypeLabel = (type, t) => {
  if (!type) return t ? t('unknown') : 'Unknown';
  if (!t) return toTitle(type);
  const key = norm(type);
  return t(key) || toTitle(type);
};

export const normalizeAgent = (raw, t, locale = 'en-US') => {
  const id = raw?.agent_id ?? raw?.id ?? raw?._id ?? '';
  const name = raw?.agent_name ?? raw?.name ?? (t ? t('untitledAgent') : 'Untitled Agent');
  const typeRaw = raw?.agent_type ?? raw?.type ?? 'unknown';
  const type = norm(typeRaw) || 'unknown';
  const is_active = Boolean(raw?.is_active);
  const status = is_active ? (t ? t('active') : 'active') : (t ? t('inactive') : 'inactive');
  const total_calls = num(raw?.total_calls ?? raw?.stats?.calls ?? raw?.call_count);
  let sr = raw?.success_rate ?? raw?.stats?.successRate ?? 0;
  if (typeof sr === 'string') sr = sr.replace('%', '');
  sr = num(sr);
  sr = sr <= 1 ? sr * 100 : sr;
  const success_rate = clamp0to100(sr);
  let acd = raw?.average_call_duration ?? raw?.stats?.avgCallDuration ?? 0;
  const average_call_duration = typeof acd === 'string' && acd.includes(':') ? acd : toMMSS(acd);
  const created_at = formatDate(raw?.created_at ?? raw?.createdAt, locale);
  return {
    id,
    name,
    type,
    is_active,
    status,
    total_calls,
    success_rate,
    average_call_duration,
    created_at,
  };
};

export const normalizeAgentForEdit = (raw = {}, fallbackEmail = '') => ({
  id: raw.agent_id ?? String(raw.id ?? raw._id ?? ''),
  name: raw.agent_name ?? raw.name ?? 'Untitled Agent',
  phone: raw.twilio_number ?? raw.contact_phone_number ?? raw.phone ?? '',
  email: raw.user_email ?? raw.email ?? fallbackEmail,
  firstMessage: raw.first_message ?? raw.first_message_to_caller ?? '',
  companyName: raw.business_name ?? raw.company_name ?? '',
});

export const formatStatus = (status) =>
(status
  ? status.toString().replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
  : 'Active');

export const fmtRel = (dtLike) => {
  try {
    const d = new Date(dtLike);
    const diff = (Date.now() - d.getTime()) / 1000;
    const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
    if (diff < 60) return rtf.format(-Math.round(diff), 'second');
    if (diff < 3600) return rtf.format(-Math.round(diff / 60), 'minute');
    if (diff < 86400) return rtf.format(-Math.round(diff / 3600), 'hour');
    return rtf.format(-Math.round(diff / 86400), 'day');
  } catch { return ''; }
};

export const normalizeRate = (v) => {
  if (v == null || v === '') return 0;
  const n = typeof v === 'string' ? parseFloat(v.replace('%', '').trim()) : Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n));
};

export const isSuperAdminRole = (role) => /super\s*admin/i.test((role ?? '').toString().replace(/_/g, ' ').trim());

export const getStatusBadge = (status) => getStatusColor(status);

export const todayDateLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const nowHHMMLocal = () => {
  const d = new Date();
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const toISOWithOffset = (date) => {
  const pad2 = (n) => String(n).padStart(2, '0');
  const yyyy = date.getFullYear();
  const mm = pad2(date.getMonth() + 1);
  const dd = pad2(date.getDate());
  const hh = pad2(date.getHours());
  const mi = pad2(date.getMinutes());
  const ss = pad2(date.getSeconds());
  const ms = String(date.getMilliseconds()).padStart(3, '0');

  const offMin = -date.getTimezoneOffset();
  const sign = offMin >= 0 ? '+' : '-';
  const abs = Math.abs(offMin);
  const offH = pad2(Math.floor(abs / 60));
  const offM = pad2(abs % 60);
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}:${ss}.${ms}${sign}${offH}:${offM}`;
};

const SERVER_TZ = 'Asia/Karachi';
export const formatYmdHmInTZ = (date, timeZone = SERVER_TZ) => {
  const dtf = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const parts = dtf.formatToParts(date);
  const pick = (t) => parts.find((p) => p.type === t)?.value ?? '';
  const yyyy = pick('year');
  const mm = pick('month');
  const dd = pick('day');
  const hh = pick('hour');
  const mi = pick('minute');
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
};

export const CheckBadge = () => (
  <span className="absolute right-2 top-2 inline-flex items-center justify-center h-5 w-5 rounded-full bg-primary-600 text-white text-[10px] shadow-sm">
    ✓
  </span>
);

export const KPISkeleton = () => (
  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
    {[...Array(4)].map((_, i) => (
      <div key={i} className="controlled-glass rounded-2xl p-6 h-32 animate-pulse overflow-hidden">
        <div className="flex items-center">
          <div className="h-10 w-10 rounded-xl bg-white/5" />
          <div className="ml-5 flex-1 space-y-2">
            <div className="h-3 w-24 bg-white/5 rounded" />
            <div className="h-5 w-16 bg-white/10 rounded" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

export const TableSkeleton = ({ rows = 6, compactPad = 'px-6 py-4' }) => (
  <div className="controlled-glass rounded-2xl overflow-hidden animate-pulse">
    <div className="overflow-x-auto">
      <div className="min-w-full">
        <div className="bg-white/5">
          <div className={`grid grid-cols-5 ${compactPad}`}>
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-3 w-24 bg-white/10 rounded" />
            ))}
          </div>
        </div>
        {[...Array(rows)].map((_, i) => (
          <div key={i} className={`grid grid-cols-5 ${compactPad} border-b border-white/5`}>
            {[...Array(5)].map((__, j) => (
              <div key={j} className="h-4 w-28 bg-white/5 rounded" />
            ))}
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const ListSkeleton = ({ rows = 3 }) => (
  <div className="space-y-4">
    {[...Array(rows)].map((_, i) => (
      <div key={i} className="controlled-glass rounded-2xl p-6 animate-pulse">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center min-w-0 flex-1">
            <div className="h-10 w-10 rounded-xl bg-white/5 mr-4" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-40 bg-white/10 rounded" />
              <div className="h-3 w-28 bg-white/5 rounded" />
            </div>
          </div>
          <div className="h-6 w-20 rounded-full bg-white/10" />
        </div>
      </div>
    ))}
  </div>
);

export const ActivitySkeleton = () => (
  <div className="space-y-4">
    {[...Array(4)].map((_, i) => (
      <div key={i} className="flex items-center space-x-4 p-4 rounded-2xl bg-white/5 animate-pulse">
        <div className="h-8 w-8 rounded-lg bg-white/10" />
        <div className="flex-1 space-y-2">
          <div className="h-4 w-48 bg-white/10 rounded" />
          <div className="h-3 w-32 bg-white/5 rounded" />
        </div>
        <div className="h-3 w-16 bg-white/5 rounded" />
      </div>
    ))}
  </div>
);

export const RefreshButton = ({ onClick, isLoading, className = "btn-secondary", children, t }) => (
  <button
    onClick={onClick}
    className={`btn ${className}`}
    disabled={isLoading}
    title={t ? t('refreshData') : "Refresh data"}
  >
    <ArrowPathIcon className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
    <span>{children || (t ? t('refresh') : "Refresh")}</span>
  </button>
);

export const PageHeader = ({ title, subtitle, children }) => (
  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 sm:mb-5 gap-4 min-w-0">
    <div className="flex-1 min-w-0">
      <h1 className="text-xl sm:text-xl font-bold text-white tracking-tight truncate">{title}</h1>
      {subtitle && <p className="mt-1 sm:mt-1 text-[10px] sm:text-[11px] text-white/40 font-medium italic leading-relaxed">{subtitle}</p>}
    </div>
    {children && <div className="flex flex-wrap items-center gap-2 sm:gap-2.5">{children}</div>}
  </div>
);

export const CustomDropdown = ({ value, onChange, options, placeholder, className = "" }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.value === value);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-5 sm:px-4 py-3.5 sm:py-2 bg-white/5 border border-white/10 rounded-2xl sm:rounded-lg text-white font-bold focus:outline-none focus:ring-4 focus:ring-primary-500/20 transition-all inner-highlight"
      >
        <span className={selectedOption ? "text-white sm:text-xs" : "text-white/40 text-sm sm:text-xs"}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDownIcon className={`h-4 w-4 sm:h-3.5 sm:w-3.5 text-white/40 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute left-0 right-0 mt-2 rounded-2xl sm:rounded-xl border border-white/10 bg-[#0a0f1d] backdrop-blur-xl shadow-2xl z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
          <div className="p-2 space-y-1">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange({ target: { value: option.value } });
                  setIsOpen(false);
                }}
                className={`flex items-center w-full px-4 py-3 sm:py-2 text-sm font-bold rounded-xl sm:rounded-lg transition-colors whitespace-nowrap ${value === option.value ? 'bg-primary-600/20 text-primary-400' : 'text-white/70 hover:text-white hover:bg-white/5'}`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const SearchAndFilters = ({
  searchTerm,
  onSearchChange,
  searchPlaceholder = "Search...",
  filters = [],
  disabled = false
}) => (
  <div className="controlled-glass rounded-[24px] sm:rounded-xl p-5 sm:p-3.5 mb-8 sm:mb-5 inner-highlight relative z-[20]">
    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
      <div className="relative flex-1 max-w-lg group">
        <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-white/20 group-focus-within:text-white transition-colors" />
        <input
          type="text"
          placeholder={searchPlaceholder}
          value={searchTerm}
          onChange={onSearchChange}
          className="input pl-11"
          disabled={disabled}
        />
      </div>
      {filters.length > 0 && (
        <div className="flex flex-wrap gap-4">
          {filters.map((filter, index) => (
            <CustomDropdown
              key={index}
              value={filter.value}
              onChange={filter.onChange}
              options={filter.options}
              className="w-full sm:w-[200px]"
            />
          ))}
        </div>
      )}
    </div>
  </div>
);

export const StatCard = ({ title, value, icon: Icon, color = 'primary', subtext, format = 'number' }) => {
  const formatValue = () => {
    switch (format) {
      case 'percentage':
        return `${num(value)}%`;
      case 'duration':
        return toMMSS(value);
      default:
        return compact(value);
    }
  };

  return (
    <div className="controlled-glass rounded-[24px] sm:rounded-xl p-4 sm:p-3.5 inner-highlight h-full flex flex-col justify-between group hover:border-white/20 transition-all">
      <div className="flex justify-between items-start mb-2 sm:mb-1.5">
        <div className="flex items-center gap-3 sm:gap-2.5">
          <div className="p-2 sm:p-1.5 rounded-xl sm:rounded-lg bg-white/5 border border-white/10 group-hover:bg-white/10 transition-colors">
            <Icon className="h-4 w-4 sm:h-3.5 sm:w-3.5 text-white/70" />
          </div>
          <span className="text-[10px] sm:text-[9px] font-bold text-white/40 uppercase tracking-widest">{title}</span>
        </div>
      </div>

      <div className="space-y-0.5 sm:space-y-0">
        <h3 className="text-2xl sm:text-[18px] font-bold text-white tracking-tight leading-tight">{formatValue()}</h3>
        {subtext && <p className="text-[9px] sm:text-[8px] text-white/30 font-semibold uppercase tracking-widest mt-0.5">{subtext}</p>}
      </div>
    </div>
  );
};

export {
  PhoneIcon,
  PlusIcon,
  EyeIcon,
  EyeSlashIcon,
  PencilIcon,
  TrashIcon,
  ResumeIcon,
  PauseIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  DocumentArrowUpIcon,
  MicrophoneIcon,
  SparklesIcon,
  LockClosedIcon,
  ChartBarIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  UserIcon,
  UserCircleIcon,
  ShieldCheckIcon,
  CogIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  MagnifyingGlassIcon,
  Bars3Icon,
  Headphones,
  Target,
  Calendar,
  Bot,
  Users,
  BarChart3,
  UserPlus,
  PlayIcon,
  InformationCircleIcon,
  CheckIcon,
  BellIcon,
  EnvelopeIcon, // Use EnvelopeIcon instead of MailIcon
  KeyIcon,
  ServerIcon,
  DocumentCheckIcon,
  ChatBubbleLeftRightIcon,
  ExclamationCircleIcon,
  UserGroupIcon,
  ChevronDownIcon,
};